import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { navigateToAuthPreservingSlug } from '../lib/navigateToAuth';
import { getGuestToken, clearGuestToken } from '../lib/guestToken';
import { GUEST_DEFAULTS } from '../lib/constants';

interface User {
  id: string;
  displayName?: string;
  profileComplete?: boolean;
  email?: string;
}

interface GuestData {
  identifierHash: string;
  organizationId: string;
  deviceId: string;
  sessionId: string;
  totalSpent: number;
  spendingLimit: number;
  remainingAmount: number;
  currency: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isGuest: boolean;
  guestData: GuestData | null;
  isAuthenticated: boolean;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
  convertGuestToUser: (email: string, firstName: string, lastName: string, phone?: string) => Promise<void>;
  refreshGuestEligibility: () => Promise<void>;
  setGuestMode: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [guestData, setGuestData] = useState<GuestData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  // Manual guest mode activation
  const setGuestMode = async (token: string) => {
    console.log('[useAuth] 🎭 Guest mode activated manually');
    setIsGuest(true);
    setUser(null);
    
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const { identifier_hash, organization_id, device_id, session_id } = tokenData;
      
      setGuestData({
        identifierHash: identifier_hash,
        organizationId: organization_id,
        deviceId: device_id,
        sessionId: session_id || 'local',
        totalSpent: 0,
        spendingLimit: GUEST_DEFAULTS.SPENDING_LIMIT_ORE,
        remainingAmount: GUEST_DEFAULTS.SPENDING_LIMIT_ORE,
        currency: GUEST_DEFAULTS.CURRENCY
      });
      
      if (organization_id) {
        sessionStorage.setItem('guestOrgId', organization_id);
        sessionStorage.setItem('activeOrgId', organization_id);
      }
      
      refreshGuestEligibility().catch(err => {
        console.warn('[useAuth] Background eligibility refresh failed:', err);
      });
    } catch (error) {
      console.error('[useAuth] Failed to parse guest token:', error);
      setGuestData({
        identifierHash: '',
        organizationId: '',
        deviceId: '',
        sessionId: 'local',
        totalSpent: 0,
        spendingLimit: GUEST_DEFAULTS.SPENDING_LIMIT_ORE,
        remainingAmount: GUEST_DEFAULTS.SPENDING_LIMIT_ORE,
        currency: GUEST_DEFAULTS.CURRENCY
      });
    }
  };

  const refreshGuestEligibility = async () => {
    const guestToken = getGuestToken();
    if (!guestToken) {
      setIsGuest(false);
      setGuestData(null);
      return;
    }

    try {
      const tokenPayload = JSON.parse(atob(guestToken.split('.')[1]));
      const { identifier_hash, organization_id, device_id, session_id, exp } = tokenPayload;

      if (exp && exp < Date.now()) {
        clearGuestToken();
        setIsGuest(false);
        setGuestData(null);
        return;
      }

      setIsGuest(true);

      const { data, error } = await supabase.functions.invoke('check-guest-eligibility', {
        body: {
          identifierHash: identifier_hash,
          organizationId: organization_id,
          deviceId: device_id
        }
      });

      if (error) {
        console.error('[useAuth] API error but keeping guest state:', error);
        setGuestData({
          identifierHash: identifier_hash,
          organizationId: organization_id,
          deviceId: device_id,
          sessionId: session_id || 'local',
          totalSpent: 0,
          spendingLimit: 20000,
          remainingAmount: 20000,
          currency: 'SEK'
        });
        return;
      }

      const isEligible = data?.eligible || data?.canShopAsGuest || data?.valid;
      
      if (!isEligible && data?.remainingAmount !== undefined && data.remainingAmount <= 0) {
        setGuestData({
          identifierHash: identifier_hash,
          organizationId: organization_id,
          deviceId: device_id,
          sessionId: data.sessionId || session_id || 'local',
          totalSpent: data.totalSpent || 0,
          spendingLimit: data.spendingLimit || 20000,
          remainingAmount: 0,
          currency: data.currency || 'SEK'
        });
        return;
      }

      setGuestData({
        identifierHash: identifier_hash,
        organizationId: organization_id,
        deviceId: device_id,
        sessionId: data?.sessionId || session_id || 'local',
        totalSpent: data?.totalSpent || 0,
        spendingLimit: data?.spendingLimit || 20000,
        remainingAmount: data?.remainingAmount ?? 20000,
        currency: data?.currency || 'SEK'
      });
    } catch (error) {
      console.error('[useAuth] Error parsing guest token:', error);
      clearGuestToken();
      setIsGuest(false);
      setGuestData(null);
    }
  };

  // Load user profile from database
  const loadUserProfile = async (userId: string, email?: string | null) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles_v2')
        .select('profile_complete, display_name, email')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('[useAuth] Profile fetch error:', profileError);
        const code = (profileError as any)?.code ?? (profileError as any)?.details;
        
        if (code === 'PGRST116' || (profileError as any)?.message?.toLowerCase?.().includes('no rows')) {
          setUser({
            id: userId,
            email: email || undefined,
            displayName: email || undefined,
            profileComplete: true,
          });
        } else {
          setUser({
            id: userId,
            email: email || undefined,
            displayName: email || undefined,
            profileComplete: false
          });
        }
        return;
      }
      
      if (!profile) {
        // Ingen profil = användaren behöver onboarding, INTE utloggning
        // Supabase session är giltig - profil är applikationsdata
        console.log('[useAuth] No profile row found - treating as incomplete profile');
        setUser({
          id: userId,
          email: email || undefined,
          displayName: email || undefined,
          profileComplete: false
        });
        return;
      }

      console.log('[useAuth] ✅ Profile loaded', {
        profileComplete: profile.profile_complete,
        displayName: profile.display_name
      });
      
      setUser({
        id: userId,
        email: email || profile.email || undefined,
        displayName: profile.display_name || email || undefined,
        profileComplete: profile.profile_complete === true
      });
    } catch (fetchError) {
      console.error('[useAuth] Profile fetch exception:', fetchError);
      setUser({
        id: userId,
        email: email || undefined,
        displayName: email || undefined,
        profileComplete: false
      });
    }
  };

  // Clear guest state and link purchases when user authenticates
  // Also cleans up any orphaned guest sessions matching user's personal_number_hash
  const handleAuthenticatedUser = async (userId: string) => {
    const guestToken = getGuestToken();
    
    // ALWAYS call cleanup function for authenticated users to handle:
    // 1. Direct BankID login (no guest token but may have matching guest session)
    // 2. Guest token present (convert flow)
    console.log('[useAuth] 🧹 Cleaning up guest state for authenticated user');
    
    try {
      // Call database function to clean up matching guest sessions by personal_number_hash
      // This handles the case where user logged in via BankID directly without going through convert flow
      const { data: cleanupResult, error: cleanupError } = await supabase
        .rpc('cleanup_guest_session_for_user', { user_id_param: userId });
      
      if (cleanupError) {
        console.warn('[useAuth] Guest cleanup RPC error:', cleanupError);
      } else if (cleanupResult?.linked_orders > 0 || cleanupResult?.cleaned_sessions > 0) {
        console.log('[useAuth] ✅ Cleaned up guest data:', cleanupResult);
        if (cleanupResult.linked_orders > 0) {
          toast.success(`${cleanupResult.linked_orders} tidigare köp har kopplats till ditt konto!`);
        }
      }
    } catch (cleanupErr) {
      console.warn('[useAuth] Error in guest cleanup:', cleanupErr);
    }
    
    // Also handle current guest token if present (for immediate session cleanup)
    if (guestToken || isGuest) {
      try {
        const tokenData = guestToken ? JSON.parse(atob(guestToken.split('.')[1])) : null;
        const guestSessionId = tokenData?.session_id;
        
        if (guestSessionId && userId) {
          const { data: linkedOrders, error: linkError } = await supabase
            .from('trade_orders')
            .update({ user_id: userId })
            .eq('guest_session_id', guestSessionId)
            .is('user_id', null)
            .select('id');
          
          if (!linkError && linkedOrders?.length) {
            console.log('[useAuth] ✅ Linked', linkedOrders.length, 'guest purchases from current session');
          }
        }
      } catch (linkErr) {
        console.error('[useAuth] Error linking guest purchases:', linkErr);
      }
      
      setIsGuest(false);
      setGuestData(null);
      clearGuestToken();
      
      try {
        const cartStore = localStorage.getItem('trade-cart-storage');
        if (cartStore) {
          const parsed = JSON.parse(cartStore);
          parsed.state.items = [];
          localStorage.setItem('trade-cart-storage', JSON.stringify(parsed));
        }
      } catch (error) {
        console.error('[useAuth] Failed to clear cart:', error);
      }
    }
  };

  const refreshAuth = async () => {
    console.log('[useAuth] 🔄 Refreshing auth state...');
    
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[useAuth] Session error:', error);
        setUser(null);
        setIsAuthenticated(false);
        await refreshGuestEligibility();
        setLoading(false);
        return;
      }

      if (!session?.user) {
        console.log('[useAuth] No active session');
        setUser(null);
        setIsAuthenticated(false);
        await refreshGuestEligibility();
        setLoading(false);
        return;
      }
      
      console.log('[useAuth] ✅ Session found for user:', session.user.id);
      setIsAuthenticated(true);
      
      await handleAuthenticatedUser(session.user.id);
      await loadUserProfile(session.user.id, session.user.email);
      
    } catch (error) {
      console.error('[useAuth] Failed to refresh auth:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Initialize auth - SUPABASE BEST PRACTICE
  // 1. Set up onAuthStateChange listener FIRST
  // 2. Then call getSession()
  useEffect(() => {
    mountedRef.current = true;
    console.log('[useAuth] Initializing auth');

    // Check for guest token on mount
    const guestToken = getGuestToken();
    if (guestToken) {
      try {
        const tokenData = JSON.parse(atob(guestToken.split('.')[1]));
        const { organization_id, exp } = tokenData;
        
        if (!exp || exp >= Date.now()) {
          console.log('[useAuth] Found valid guest token on mount');
          setIsGuest(true);
          if (organization_id) {
            sessionStorage.setItem('guestOrgId', organization_id);
            sessionStorage.setItem('activeOrgId', organization_id);
            localStorage.setItem('guest-organization-id', organization_id);
          }
        } else {
          clearGuestToken();
        }
      } catch (error) {
        console.error('[useAuth] Error parsing guest token on mount:', error);
        clearGuestToken();
      }
    }

    // 1. Set up auth state listener FIRST (critical order!)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mountedRef.current) return;
      
      console.log('[useAuth] 🔔 Auth event:', event, { hasSession: !!session });
      
      if (session?.user) {
        setIsAuthenticated(true);
        // Defer profile loading with setTimeout(0) to avoid deadlock
        setTimeout(() => {
          if (mountedRef.current) {
            handleAuthenticatedUser(session.user.id);
            loadUserProfile(session.user.id, session.user.email);
          }
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        // Check guest eligibility after sign out
        setTimeout(() => {
          if (mountedRef.current) {
            refreshGuestEligibility();
          }
        }, 0);
      }
    });

    // 2. THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (!mountedRef.current) return;
      
      if (error) {
        console.error('[useAuth] Initial session error:', error);
        setLoading(false);
        refreshGuestEligibility();
        return;
      }
      
      if (session?.user) {
        console.log('[useAuth] ✅ Initial session found:', session.user.id);
        setIsAuthenticated(true);
        await handleAuthenticatedUser(session.user.id);
        await loadUserProfile(session.user.id, session.user.email);
      } else {
        console.log('[useAuth] No initial session');
        await refreshGuestEligibility();
      }
      
      setLoading(false);
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const convertGuestToUser = async (email: string, firstName: string, lastName: string, phone?: string) => {
    if (!guestData) {
      throw new Error('No guest session to convert');
    }

    try {
      const orgContext = sessionStorage.getItem('org_context');
      const orgData = orgContext ? JSON.parse(orgContext) : null;

      const { data, error } = await supabase.functions.invoke('convert-guest-to-user', {
        body: {
          guestSessionId: guestData.sessionId,
          identifierHash: guestData.identifierHash,
          email,
          firstName,
          lastName,
          phone: phone || '',
          organizationId: orgData?.org_id || guestData.organizationId
        }
      });

      if (error || !data?.success) {
        if (data?.error === 'BANKID_ALREADY_REGISTERED' || data?.code === 'BANKID_ALREADY_REGISTERED') {
          const message = `Detta BankID är redan kopplat till kontot ${data.existing_email || 'ett befintligt konto'}.`;
          toast.error(message, { duration: 8000 });
          throw new Error(message);
        }
        throw error || new Error('Failed to convert guest to user');
      }

      setIsGuest(false);
      setGuestData(null);
      clearGuestToken();

      toast.success('Konto skapat! Du är nu inloggad.');
      
      await supabase.auth.refreshSession();
      await refreshAuth();
    } catch (error) {
      console.error('[useAuth] Convert guest error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    console.log('[useAuth] Signing out');
    
    try {
      const orgContext = sessionStorage.getItem('org_context');
      let orgSlug: string | null = null;
      
      if (user?.id) {
        try {
          const { data: primaryDeviceData } = await supabase
            .from('customer_devices')
            .select('device_id')
            .eq('user_id', user.id)
            .eq('is_primary', true)
            .maybeSingle();
            
          if (primaryDeviceData?.device_id) {
            const { data: deviceData } = await supabase
              .from('ziplink_devices')
              .select('tenant_id')
              .eq('id', primaryDeviceData.device_id)
              .maybeSingle();
              
            if (deviceData?.tenant_id) {
              const { data: orgData } = await supabase
                .from('organizations')
                .select('slug')
                .eq('tenant_id', deviceData.tenant_id)
                .maybeSingle();
                
              if (orgData?.slug) {
                orgSlug = orgData.slug;
              }
            }
          }
        } catch (e) {
          console.warn('[useAuth] Failed to get primary device org:', e);
        }
      }
      
      if (!orgSlug && orgContext) {
        try {
          const ctx = JSON.parse(orgContext);
          orgSlug = ctx?.org_slug || null;
        } catch (e) {
          console.error('[useAuth] Failed to parse org_context:', e);
        }
      }
      
      if (orgSlug) {
        sessionStorage.setItem('last_slug_path', `/${orgSlug}`);
      }
      
      if (isGuest) {
        setIsGuest(false);
        setGuestData(null);
        clearGuestToken();
      }
      
      // Clear all session artifacts
      try {
        localStorage.removeItem('pwa-guest-token');
        sessionStorage.removeItem('pwa-guest-token');
        localStorage.removeItem('guest-organization-id');
        localStorage.removeItem('bankid_orderRef');
        sessionStorage.removeItem('bankid_orderRef');
        sessionStorage.removeItem('bankid_trace_id');
        sessionStorage.removeItem('bankid_auth_complete');
        sessionStorage.removeItem('bankid_auth_timestamp');
        sessionStorage.removeItem('bankid_data');
        sessionStorage.removeItem('bankid_personal_number_hash');
        localStorage.removeItem('bankid_data');
        localStorage.removeItem('bankid_personal_number_hash');
        localStorage.removeItem('bankid_timestamp');
        sessionStorage.removeItem('profile_complete_confirmed');
        localStorage.removeItem('pwa-handel-auth');
        localStorage.removeItem('trade-cart-storage');
        localStorage.removeItem('trade-cart-deviceId');
        localStorage.removeItem('selectedDeviceCode');
        sessionStorage.removeItem('selected_device_id');
        sessionStorage.removeItem('onboarding_device_code');
        sessionStorage.removeItem('selected_device_name');
      } catch (e) {
        console.warn('[useAuth] Failed to clear some session artifacts:', e);
      }
      
      localStorage.setItem('pwa-logout-at', Date.now().toString());
      sessionStorage.setItem('pwa-just-logged-out', 'true');
      
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      if (error) {
        console.warn('[useAuth] Sign out error (continuing):', error);
      }
      
      try {
        await fetch('https://auth.ziplink.se/logout', {
          method: 'POST',
          credentials: 'include',
        });
      } catch (e) {
        console.warn('[useAuth] Remote logout failed (non-fatal):', e);
      }
      
      setUser(null);
      setIsAuthenticated(false);
      
      if (orgContext) {
        sessionStorage.setItem('org_context', orgContext);
      }
      
      if (orgSlug) {
        navigate(`/${orgSlug}`, { replace: true });
      } else {
        navigateToAuthPreservingSlug(navigate);
      }
    } catch (error) {
      console.error('[useAuth] Sign out error:', error);
      toast.error('Kunde inte logga ut');
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isGuest, 
      guestData,
      isAuthenticated,
      refreshAuth, 
      signOut, 
      convertGuestToUser,
      refreshGuestEligibility,
      setGuestMode
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
